import React, { useEffect, useMemo, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { MdOutlineClear } from 'react-icons/md';
import { RiArrowRightSLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import Breadcrumb from '../../components/CoreComponents/Breadcrumb';
import {
    getDrilldownType,
    orangeRowHoverClassName,
    pathDelmiter,
    selectedRowClassName,
    Status,
    tasksddLastClickedKey,
    tasksddScrollKey,
} from '../../Constants/Consts';
import { getByPath, getLocalStorageItem, removeLocalStorageItem, replaceLastOccurrence, setLocalStorageItem } from '../../Helpers/Functions';
import { RootState } from '../../store/store';
import { setBackClickHandler, setTaskDisplayedPath } from '../../store/Utilities';

function collectStatusesFromSubtree(node: any): number[] {
    const statuses: number[] = [];
    if (node.taskChilds) {
        statuses.push(...Object.values(node.taskChilds).map((c: any) => c.statusId));
    }
    if (node.childs) {
        Object.values(node.childs).forEach((child) => {
            statuses.push(...collectStatusesFromSubtree(child));
        });
    }
    return statuses;
}

function collectCtUuidsFromFailedTasks(node: any): string[] {
    const uuids: string[] = [];
    if (node.taskChilds) {
        Object.values(node.taskChilds).forEach((c: any) => {
            if (c.statusId === Status.Failed && c.ctUuid) {
                uuids.push(c.ctUuid.toUpperCase());
            }
        });
    }
    if (node.checkItems) {
        Object.values(node.checkItems).forEach((c: any) => {
            if (c.statusId === Status.Failed && c.ctUuid) {
                uuids.push(c.ctUuid.toUpperCase());
            }
        });
    }
    if (node.childs) {
        Object.values(node.childs).forEach((child) => {
            uuids.push(...collectCtUuidsFromFailedTasks(child));
        });
    }
    return uuids;
}

function buildStatusBadge(statuses: number[]): JSX.Element {
    if (statuses.length === 0) return <></>;
    const allNotStarted = statuses.every(s => s === 1);
    const allCompletedOrNA = statuses.every(s => s === 2 || s === 5);
    const passedTotal = statuses.filter(s => s === 2).length;
    const failedTotal = statuses.filter(s => s === 3).length;
    const openTotal = statuses.filter(s => s === 1).length;
    const Dash = <span className='px-3'>-</span>;
    if (allNotStarted) return <>{Dash} <span className="bg-gray-200 text-gray-800 py-1 px-3 rounded-full">Not Started</span></>;
    if (allCompletedOrNA) return <>{Dash} <span className="bg-green-200 text-green-800 py-1 px-3 rounded-full">Completed</span></>;
    return (
        <div className='inline-flex items-center'>
            {Dash}
            {!!passedTotal && <span className="bg-green-200 text-green-800 py-1 px-3 rounded-full">{passedTotal} Passed</span>}
            {!!failedTotal && <span className="bg-red-200 text-yellow-800 py-1 px-3 rounded-full ml-2">{failedTotal} Failed</span>}
            {!!openTotal && <span className="bg-gray-500 text-white py-1 px-3 rounded-full ml-2">{openTotal} Open</span>}
        </div>
    );
}

export default function TasksDD() {
    const { ChecklistsObject, DrilldownMappingsAPIResponse } = useSelector((state: RootState) => state.SequenceObject);
    const { SelectedUser } = useSelector((state: RootState) => state.SelectedUser);
    const { taskDisplayedPath } = useSelector((state: RootState) => state.Utilities);
    const { ProjectPunchLists } = useSelector((state: RootState) => state.PunchList);
    const drilldownType = getDrilldownType(SelectedUser.disciplineId)
    // Walk down the tree to find a leaf checklist item's drilldownTypeId
    const findActualDrilldownType = (obj: any): number | null => {
        if (!obj || typeof obj !== 'object') return null;
        if (obj.taskChilds) {
            const firstTask = Object.values(obj.taskChilds)[0] as any;
            if (firstTask?.drilldownTypeId != null) return firstTask.drilldownTypeId;
        }
        if (obj.childs) {
            for (const key of Object.keys(obj.childs)) {
                const result = findActualDrilldownType(obj.childs[key]);
                if (result != null) return result;
            }
        }
        // Also check direct children (for when we're already inside childs)
        for (const key of Object.keys(obj)) {
            if (key !== 'childs' && key !== 'taskChilds' && typeof obj[key] === 'object') {
                const result = findActualDrilldownType(obj[key]);
                if (result != null) return result;
            }
        }
        return null;
    };
    const actualDrilldownType = findActualDrilldownType(ChecklistsObject) ?? drilldownType;
    const DrilldownMappingObject = DrilldownMappingsAPIResponse.find(d => d.drilldownTypeId === actualDrilldownType)
    const [displayedObject, setDisplayedObject] = useState({})
    const isPathToShowTasks = (path) => {
        const pathArrayToNode = path.split(pathDelmiter)
        const access = pathArrayToNode?.pop()
        if (access !== "childs" && access !== "taskChilds") {
            return
        }
        const pathToNode = pathArrayToNode?.join(pathDelmiter)
        if (!pathToNode) return
        const currentObject = getByPath(pathToNode, ChecklistsObject)
        return Object.keys(currentObject.childs ?? {}).length === 0
    }
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // Record the clicked row + current scroll position for this level before
    // drilling in or navigating to the checklist page. rowKey uniquely
    // identifies the row (a unit can render two rows: a drill row and a tasks
    // row) so only the actual clicked row is highlighted on return.
    const rememberClick = (rowKey: string) => {
        const clickedMap = getLocalStorageItem<Record<string, string>>(tasksddLastClickedKey) ?? {};
        clickedMap[taskDisplayedPath] = rowKey;
        setLocalStorageItem(tasksddLastClickedKey, clickedMap);
        const scrollMap = getLocalStorageItem<Record<string, number>>(tasksddScrollKey) ?? {};
        scrollMap[taskDisplayedPath] = window.scrollY;
        setLocalStorageItem(tasksddScrollKey, scrollMap);
    }

    const lastClickedAtLevel = getLocalStorageItem<Record<string, string>>(tasksddLastClickedKey)?.[taskDisplayedPath];
    const rowClassName = (rowKey: string) =>
        twMerge(orangeRowHoverClassName, rowKey === lastClickedAtLevel ? selectedRowClassName : "");

    useEffect(() => {
        if (isPathToShowTasks(taskDisplayedPath)) {
            const vv = replaceLastOccurrence(taskDisplayedPath, pathDelmiter, "childs", "taskChilds")
            const f = encodeURIComponent(vv)
            navigate(`/CheckListDD/${f}`)
        }
        const obj = getByPath(taskDisplayedPath, ChecklistsObject)
        setDisplayedObject(obj)
    }, [taskDisplayedPath])

    useEffect(() => {
        dispatch(setBackClickHandler(() => {
            const pathArray = taskDisplayedPath.split(pathDelmiter)
            if (pathArray.length === 1 && pathArray[0] === "childs")
                navigate(-1)
            if (pathArray.length > 1) {
                pathArray.pop() // pop the childs keyword
                pathArray.pop() // pop the path step
                const previousPath = pathArray.join(pathDelmiter)
                dispatch(setTaskDisplayedPath(previousPath))
            }
        }))
        return () => {
            dispatch(setBackClickHandler(null))
        }
    }, [taskDisplayedPath])

    // Reset the drill-down highlight + scroll when leaving the Tasks section
    // entirely. Preserved while navigating into the checklist (/CheckListDD/) so
    // the highlight survives the drill-in -> Back round-trip. Without this the
    // per-level selection map persists indefinitely (even across sessions), so
    // every level would keep showing the last row ever clicked.
    useEffect(() => {
        return () => {
            const goingIntoChecklist = window.location.pathname.includes("/CheckListDD/");
            if (!goingIntoChecklist) {
                removeLocalStorageItem(tasksddLastClickedKey);
                removeLocalStorageItem(tasksddScrollKey);
            }
        };
    }, []);

    // Restore scroll position for the current level once its rows have rendered.
    useEffect(() => {
        if (Object.keys(displayedObject).length === 0) return;
        const scrollMap = getLocalStorageItem<Record<string, number>>(tasksddScrollKey);
        const savedY = scrollMap?.[taskDisplayedPath];
        if (!savedY) return;
        requestAnimationFrame(() => window.scrollTo(0, savedY));
    }, [taskDisplayedPath, displayedObject])

    const handleGetCurrentDDLevels = () => {
        const pathArray = taskDisplayedPath.split(pathDelmiter)
            .filter(p => p !== "childs")
            .map(p => p === "checkItems" ? "I/O Checks" : p);
        const totaltDDNum = pathArray[0]?.length === 0 ? 0 : pathArray.length
        let currentDDSLevels = [];
        for (let i = 0; i < totaltDDNum; i++) {
            const ddLabel = DrilldownMappingObject?.[`dd0${i + 1}`] ?? '';
            const ddValue = pathArray[i] ?? '';
            if (ddValue) {
                if (currentDDSLevels.length > 0) {
                    currentDDSLevels.push({ label: <RiArrowRightSLine />, icon: true })
                }
                currentDDSLevels.push({ label: ddLabel ? <><strong>{ddLabel}</strong>{` : ${ddValue}`}</> : ddValue })
            }
        }
        return currentDDSLevels
    };

    const ddLevels = handleGetCurrentDDLevels();
    const breadcrumbTrail = [
        { label: SelectedUser.projectNumber },
        { label: ' - ' },
        { label: SelectedUser.project },
        { label: <RiArrowRightSLine />, icon: true },
        { label: SelectedUser.discipline },
    ];
    const secondaryTrail = ddLevels;
    const [searchTerm, setSearchTerm] = useState("")
    const initialArray = Object.keys(displayedObject)
    const filteredObjectData = initialArray.filter(k => k.toLowerCase().includes(searchTerm.trim().toLowerCase()))

    const incompletePLStatuses = [Status.ReadyForReInspection, Status.Pending, Status.OnHold, Status.MaterialsReceived, Status.CompletedWithRootCauseConflict];

    const itemStatusMap = useMemo(() => {
        const map: Record<string, { unitStatus: JSX.Element; childsStatus: JSX.Element; unitIncompletePLCount: number; childsIncompletePLCount: number }> = {};
        for (const key of Object.keys(displayedObject)) {
            const node = (displayedObject as any)[key];
            const unitStatuses: number[] = node?.taskChilds
                ? Object.values(node.taskChilds).map((c: any) => c.statusId)
                : [];
            let childsStatuses: number[] = [];
            if (node?.checkItems) {
                childsStatuses = Object.values(node.checkItems).map((c: any) => c.statusId);
            } else if (node?.childs) {
                childsStatuses = Object.values(node.childs).flatMap(collectStatusesFromSubtree);
            }

            // Count incomplete PLs for taskChilds (unit tasks) only
            const unitFailedUuids: string[] = node?.taskChilds
                ? Object.values(node.taskChilds).filter((c: any) => c.statusId === Status.Failed && c.ctUuid).map((c: any) => c.ctUuid.toUpperCase())
                : [];
            const seenUnit = new Set<string>();
            const unitIncompletePLCount = unitFailedUuids.length > 0
                ? ProjectPunchLists.filter(pl => {
                    if (!unitFailedUuids.includes(pl.ctUuid?.toUpperCase()) ||
                        !incompletePLStatuses.includes(pl.statusId) ||
                        pl.isArchived) return false;
                    const plId = pl.punchListUuid?.toUpperCase();
                    if (plId && seenUnit.has(plId)) return false;
                    if (plId) seenUnit.add(plId);
                    return true;
                }).length
                : 0;

            // Count incomplete PLs for checkItems/childs only
            const childsFailedUuids: string[] = node?.checkItems
                ? Object.values(node.checkItems).filter((c: any) => c.statusId === Status.Failed && c.ctUuid).map((c: any) => c.ctUuid.toUpperCase())
                : (node?.childs ? collectCtUuidsFromFailedTasks({ childs: node.childs }) : []);
            const seenChilds = new Set<string>();
            const childsIncompletePLCount = childsFailedUuids.length > 0
                ? ProjectPunchLists.filter(pl => {
                    if (!childsFailedUuids.includes(pl.ctUuid?.toUpperCase()) ||
                        !incompletePLStatuses.includes(pl.statusId) ||
                        pl.isArchived) return false;
                    const plId = pl.punchListUuid?.toUpperCase();
                    if (plId && seenChilds.has(plId)) return false;
                    if (plId) seenChilds.add(plId);
                    return true;
                }).length
                : 0;

            map[key] = {
                unitStatus: buildStatusBadge(unitStatuses),
                childsStatus: buildStatusBadge(childsStatuses),
                unitIncompletePLCount,
                childsIncompletePLCount,
            };
        }
        return map;
    }, [displayedObject, ProjectPunchLists]);
    return (
        <div className="ml-2">
            <Breadcrumb trail={breadcrumbTrail} showIndices={[2, 3, 4, 5, 6]} secondaryTrail={secondaryTrail} />
            <div className='flex'>
                <div className="border rounded-lg">
                    <span className="flex items-center px-2 py-3 text-sm font-medium bg-gray-50 dark:bg-gray-900 dark:text-gray-200">
                        <FaSearch />
                        <span className="ml-2 hidden md:inline-block">Search</span>
                    </span>
                </div>
                <div className="flex border rounded-lg border-1 w-full">
                    <input value={searchTerm} onChange={event => setSearchTerm(event.target.value)}
                        type="text" placeholder="Search" className="px-2 w-full" />
                    <div className="border border-black rounded-lg bg-gray-50 py-1" hidden={searchTerm === ""}>
                        <button type="button" className="py-3" title="Clear" onClick={() => setSearchTerm("")}  >
                            <MdOutlineClear />
                        </button>
                    </div>
                </div>
            </div>
            <table className="min-w-full divide-y divide-gray-200">
                <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-1000">
                    {filteredObjectData?.map((item) => {
                        const node = displayedObject[item]
                        const hasCheckItems = node.checkItems != null && Object.keys(node.checkItems).length > 0
                        const hasTwoPaths = (Object.keys(node.childs ?? {}).length > 0 || hasCheckItems)
                            && Object.keys(node.taskChilds ?? {}).length > 0
                        const childsAreCheckItems = hasCheckItems
                        if (hasTwoPaths) {
                            return <React.Fragment key={item}>
                                <tr
                                    onClick={() => {
                                        rememberClick(item)
                                        const nextPath = taskDisplayedPath ? taskDisplayedPath + pathDelmiter + item : item
                                        const pp = nextPath + pathDelmiter + (childsAreCheckItems ? "checkItems" : "childs")
                                        if (childsAreCheckItems) {
                                            navigate(`/CheckListDD/${encodeURIComponent(pp)}`)
                                            return
                                        }
                                        if (isPathToShowTasks(pp)) {
                                            const vv = replaceLastOccurrence(pp, pathDelmiter, "childs", "taskChilds")
                                            const f = encodeURIComponent(vv)
                                            navigate(`/CheckListDD/${f}`)
                                            return
                                        }
                                        dispatch(setTaskDisplayedPath(pp))
                                    }}
                                    className={rowClassName(item)}
                                >
                                    <td className="py-4 whitespace-nowrap text-left text-sm text-gray-900 dark:text-white">
                                        {item} {itemStatusMap[item]?.childsStatus}
                                        {!!itemStatusMap[item]?.childsIncompletePLCount && <span className="bg-yellow-200 text-yellow-800 py-1 px-3 rounded-full ml-2">{itemStatusMap[item].childsIncompletePLCount} Incomplete Punch List{itemStatusMap[item].childsIncompletePLCount > 1 ? 's' : ''}</span>}
                                    </td>
                                </tr>
                                <tr
                                    key={`${item}-tasks`}
                                    onClick={() => {
                                        rememberClick(`${item}-tasks`)
                                        const nextPath = taskDisplayedPath ? taskDisplayedPath + pathDelmiter + item : item
                                        // if we reached the tasks checklist objects
                                        const pp = nextPath + pathDelmiter + "taskChilds"
                                        const f = encodeURIComponent(pp)
                                        navigate(`/CheckListDD/${f}`)
                                    }}
                                    className={rowClassName(`${item}-tasks`)}
                                >
                                    <td className="py-4 whitespace-nowrap text-left text-sm text-gray-900 dark:text-white">
                                        {item}{childsAreCheckItems ? ' - Cabinet Checks' : ''} {itemStatusMap[item]?.unitStatus}
                                        {!!itemStatusMap[item]?.unitIncompletePLCount && <span className="bg-yellow-200 text-yellow-800 py-1 px-3 rounded-full ml-2">{itemStatusMap[item].unitIncompletePLCount} Incomplete Punch List{itemStatusMap[item].unitIncompletePLCount > 1 ? 's' : ''}</span>}
                                    </td>
                                </tr>
                            </React.Fragment>
                        } else
                            return (
                                <tr
                                    key={item}
                                    onClick={() => {
                                        rememberClick(item)
                                        const nextPath = taskDisplayedPath ? taskDisplayedPath + pathDelmiter + item : item
                                        const pp = nextPath + pathDelmiter + (childsAreCheckItems ? "checkItems" : "childs")
                                        // RAD: checkItems contains flattened check items — go straight to CheckListDD
                                        if (childsAreCheckItems) {
                                            navigate(`/CheckListDD/${encodeURIComponent(pp)}`)
                                            return
                                        }
                                        if (isPathToShowTasks(pp)) {
                                            const vv = replaceLastOccurrence(pp, pathDelmiter, "childs", "taskChilds")
                                            const f = encodeURIComponent(vv)
                                            navigate(`/CheckListDD/${f}`)
                                            return
                                        }
                                        dispatch(setTaskDisplayedPath(pp))
                                    }}
                                    className={rowClassName(item)}
                                >
                                    <td className="py-4 whitespace-nowrap text-left text-sm text-gray-900 dark:text-white">
                                        {item}{childsAreCheckItems ? ' Cabinet Checks' : ''} {!childsAreCheckItems && itemStatusMap[item]?.unitStatus}
                                        {!!itemStatusMap[item]?.unitIncompletePLCount && <span className="bg-yellow-200 text-yellow-800 py-1 px-3 rounded-full ml-2">{itemStatusMap[item].unitIncompletePLCount} Incomplete Punch List{itemStatusMap[item].unitIncompletePLCount > 1 ? 's' : ''}</span>}
                                    </td>
                                </tr>
                            );
                    })}
                </tbody>
            </table>
        </div>
    )
}
